/**
 * Main application entry point
 * Initializes React root with providers and renders the App
 */

// Initialize Sentry before other imports to capture early errors
import { initSentryClient, SentryErrorBoundary } from './lib/sentry';
initSentryClient();

import { createRoot } from "react-dom/client";

import './styles/themes/app-theme.css';

// Import common styles (always loaded)
// Contains Tailwind directives, base styles, utilities, and component animations
import './styles/common.css';
import './styles/typography.css';

// Load the reference font stack used for the shared typography system
// Provides a clearer body and heading contrast across the application
import '@fontsource/manrope/400.css';
import '@fontsource/manrope/500.css';
import '@fontsource/manrope/600.css';
import '@fontsource/manrope/700.css';
import '@fontsource/sora/400.css';
import '@fontsource/sora/500.css';
import '@fontsource/sora/600.css';
import '@fontsource/sora/700.css';

import App from "./app.tsx";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ErrorBoundaryFallback } from "./components/error-boundary-fallback";

/**
 * React Query client instance
 * Manages server state caching and synchronization across the application
 */
const queryClient = new QueryClient();

/**
 * React application root initialization
 * Wraps the app with essential providers for state management, theming, and server state synchronization
 */
createRoot(document.getElementById("root")!).render(
  <SentryErrorBoundary fallback={({ error, resetError }) => (
    <ErrorBoundaryFallback error={error as Error} resetError={resetError} />
  )}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </SentryErrorBoundary>
);
